import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/solid";
import { listMyPlans, deletePlan } from "../../api/advisorApi";

const Card = ({ children, className = "" }) => (
  <div
    className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const STATUS_STYLES = {
  draft:     "bg-amber-500/15 text-amber-500",
  in_review: "bg-sky-500/15 text-sky-500",
  live:      "bg-emerald-500/15 text-emerald-500",
  paused:    "bg-gray-500/15 text-gray-500",
};

const formatPrice = (minor, currency, interval) => {
  if (!Number.isFinite(minor)) return `— ${currency}/${interval}`;
  const major = minor / 100;
  return `${currency} ${major.toFixed(2)} / ${interval}`;
};

const PlanList = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setPlans(await listMyPlans());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (plan) => {
    const verb = plan.review_status === "live" ? "pause" : "delete";
    if (!window.confirm(`${verb[0].toUpperCase() + verb.slice(1)} "${plan.name}"?`)) return;
    await deletePlan(plan.id);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Plans</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
            A plan is what subscribers actually buy. Bundle one or more of your skills,
            set a price, and submit for review.
          </p>
        </div>
        <Link
          to="/advisor/plans/new"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 whitespace-nowrap"
        >
          <PlusIcon className="w-4 h-4" />
          New plan
        </Link>
      </div>

      {loading ? (
        <Card>Loading…</Card>
      ) : plans.length === 0 ? (
        <Card>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            No plans yet. Create your first skill, then bundle it into a plan that
            subscribers can buy.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {plans.map((p) => (
            <Card key={p.id}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-lg">{p.name}</h3>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_STYLES[p.review_status] || "bg-gray-500/15 text-gray-500"}`}>
                      {p.review_status}
                    </span>
                  </div>
                  <p className="text-sm font-mono mt-1">
                    {formatPrice(p.price_minor_units, p.currency, p.billing_interval)}
                  </p>
                  {p.summary && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{p.summary}</p>
                  )}
                  {(p.skill_summaries || []).length > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      Includes: {p.skill_summaries.map((s) => s.name).join(" · ")}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Link
                    to={`/advisor/plans/${p.id}`}
                    className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-white/15 text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5"
                  >
                    Edit
                  </Link>
                  <button
                    type="button"
                    onClick={() => onDelete(p)}
                    className="px-3 py-1.5 rounded-lg text-red-500 hover:bg-red-500/10 text-sm font-medium flex items-center gap-1"
                  >
                    <TrashIcon className="w-4 h-4" />
                    {p.review_status === "live" ? "Pause" : "Delete"}
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlanList;
